/**
 * Copyright 2018 Samsung Electronics All Rights Reserved
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Frest, RequestType, IRequest } from 'frest';
import {
  SbaasClient,
  SbaasEndpointType,
  SbaasClientOps,
  SbaasStdResponse,
  SbaasFile,
  UrlOptions,
  PartialRequest,
  PathParser,
  MessageSubscribeRequest,
} from './types';
import { SbaasSSE } from './sse';

export const createCommonSbaasClient = (
  http: Frest,
  parser: PathParser,
  me?: boolean,
  logout?: boolean,
  refresh?: boolean,
): SbaasClient => {
  const sse = new SbaasSSE({ base: http.base, parser });
  return {
    db: createOps(http, parser, 'db'),
    dbGen: createOps(http, parser, 'dbGen'),
    script: createOps(http, parser, 'script'),
    email: createOps(http, parser, 'email'),
    file: (endpoint: string) => {
      const path = `file/${endpoint}`;
      return {
        url: (options: UrlOptions = {}) => http.parsePath(path, options.query),
        list: (req: Partial<IRequest> = {}) =>
          http.get<SbaasStdResponse<SbaasFile[]>>({ ...req, path }),
        download: (id: string | number, req: Partial<IRequest> = {}) =>
          http.get<never>({ ...req, path, query: queryId(id, req) }),
        upload: (req: Partial<IRequest> = {}) => http.upload({ ...req, path }),
        folder: (id: string | number, req: Partial<IRequest> = {}) =>
          http.get<SbaasStdResponse<SbaasFile[]>>({
            ...req,
            path: `${path}/folder/${id}`,
          }),
        delete: (id: string | number, req: Partial<IRequest> = {}) =>
          http.delete({
            ...req,
            path: `${path}/${id}`,
          }),
      };
    },

    me: () =>
      me
        ? http.get(parser('me'))
        : Promise.reject(
            new TypeError('me endpoint is only for SBaaS v1.0 onward.'),
          ),
    login: (req: PartialRequest = {}) =>
      http.request(parser('login'), {
        ...req,
        method: 'POST',
        action: 'login',
      }),
    logout: (req: PartialRequest = {}) =>
      logout
        ? http.request(parser('logout'), {
            ...req,
            method: 'POST',
            action: 'logout',
          })
        : Promise.reject(
            new TypeError('logout endpoint is only for SBaaS v1.0 onward.'),
          ),
    refresh: (req: PartialRequest = {}) =>
      refresh
        ? http.request(parser('refresh'), {
            ...req,
            method: 'POST',
            action: 'refresh',
          })
        : Promise.reject(
            new TypeError('refresh endpoint is only for SBaaS v1.0 onward.'),
          ),

    subscribe: (req: MessageSubscribeRequest) => sse.subscribe(req),
    unsubscribe: (ticket: number | number[]) => sse.unsubscribe(ticket),
  };
};

const createOps = (
  http: Frest,
  parser: PathParser,
  type: SbaasEndpointType,
) => (endpoint: string, extra?: string): SbaasClientOps => {
  const path = parser(parsePath(type, endpoint, extra));
  return {
    url: (options: UrlOptions = {}) =>
      http.parsePath(parseRequest(path, options).path || ''),
    get: <T = any>(req?: RequestType) => http.get<T>(parseRequest(path, req)),
    post: <T = any>(req?: RequestType) => http.post<T>(parseRequest(path, req)),
    put: <T = any>(req?: RequestType) => http.put<T>(parseRequest(path, req)),
    patch: <T = any>(req?: RequestType) =>
      http.patch<T>(parseRequest(path, req)),
    delete: <T = any>(req?: RequestType) =>
      http.delete<T>(parseRequest(path, req)),
  };
};

const parsePath = (
  type: SbaasEndpointType,
  endpoint: string,
  extra?: string,
): string => {
  let path = '';
  if (type === 'dbGen') {
    if (extra) {
      path = `db/${endpoint}/gen/${extra}`;
    } else {
      throw new Error(
        'db name is required as `extra` for generic db endpoint type',
      );
    }
  } else if (type === 'email') {
    if (extra === 'html' || extra === 'text') {
      path = `${type}/${endpoint}/${extra}`;
    } else {
      throw new Error(
        'email type (html/text) is required as `extra` ' +
          'for mail endpoint type',
      );
    }
  } else {
    path = `${type}/${endpoint}`;
  }
  return path;
};

const parseRequest = (path: string, req?: RequestType): Partial<IRequest> => {
  if (typeof req === 'string') {
    return { path: `${path}/${req}` };
  } else if (Array.isArray(req)) {
    return { path: `${path}/${req.join('/')}` };
  } else if (req) {
    if (typeof req.path === 'string') {
      return { ...req, path: `${path}/${req.path}` };
    } else if (req && Array.isArray(req.path)) {
      return { ...req, path: `${path}/${req.path.join('/')}` };
    }
    return { path, ...req };
  }
  return { path };
};

const queryId = (id: any, req: Partial<IRequest>): any => {
  let query: any = {};
  if (req.query) {
    query =
      typeof req.query === 'string'
        ? req.query.length === 0
          ? `id=${id}`
          : `${req.query}&id=${id}`
        : { ...req.query, id };
  }
  return query;
};
